import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const defaultBootstrap = {
  authenticated: false,
  user_profile: null,
  is_super_admin: false,
  maintenance_mode: null,
  processes_data: [],
  weeks: [],
  default_weeks: 2,
  routes: {
    login: '/login',
    logout: '/logout',
    history: '/history',
    admin: '/admin',
    dashboard: '/',
    home: '/'
  }
};

const BootstrapContext = createContext({
  ...defaultBootstrap,
  ready: false,
  lastUpdated: 0,
  refreshBootstrap: () => {}
});

const readBootstrapPayload = () => {
  if (typeof window === 'undefined') {
    return defaultBootstrap;
  }
  const payload = window.__MODELO_PRODUCCION_BOOTSTRAP__
    || window.__APP_BOOTSTRAP__
    || defaultBootstrap;
  return {
    ...defaultBootstrap,
    ...payload
  };
};

export const BootstrapProvider = ({ children }) => {
  const initialPayload = readBootstrapPayload();
  const [data, setData] = useState(initialPayload);
  const [ready, setReady] = useState(() => hasMeaningfulPayload(initialPayload));
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const [autoFetchAttempted, setAutoFetchAttempted] = useState(false);

  const applyPayload = (payload) => {
    if (!payload || typeof payload !== 'object') {
      return;
    }
    setData({ ...defaultBootstrap, ...payload });
    setReady(hasMeaningfulPayload(payload));
    setLastUpdated(Date.now());
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleBootstrap = (event) => {
      const payload = event?.detail
        || window.__MODELO_PRODUCCION_BOOTSTRAP__
        || window.__APP_BOOTSTRAP__;
      applyPayload(payload);
    };

    // Capturar datos si el script ya se cargó
    handleBootstrap();

    window.addEventListener('modelo-bootstrap-ready', handleBootstrap);
    return () => window.removeEventListener('modelo-bootstrap-ready', handleBootstrap);
  }, []);

  const refreshBootstrap = () => {
    const payload = readBootstrapPayload();
    applyPayload(payload);
  };

  useEffect(() => {
    const needsProcesses = !Array.isArray(data.processes_data) || data.processes_data.length === 0;
    const needsWeeks = !Array.isArray(data.weeks) || data.weeks.length === 0;
    const shouldFetch = data.authenticated && (needsProcesses || needsWeeks) && !autoFetchAttempted;

    if (!shouldFetch) {
      return undefined;
    }
    if (typeof window !== 'undefined' && window.console) {
      window.console.info(
        '[Bootstrap] Payload incompleto detectado. Recuperando desde API...',
        { needsProcesses, needsWeeks }
      );
    }

    let cancelled = false;
    const fetchBootstrap = async () => {
      setAutoFetchAttempted(true);
      try {
        const response = await fetch('/api/bootstrap_payload', {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
          throw new Error(`Error ${response.status}`);
        }
        const payload = await response.json();
        if (!cancelled && payload) {
          applyPayload(payload);
          if (typeof window !== 'undefined' && window.console) {
            window.console.info(
              '[Bootstrap] Payload actualizado desde API.',
              {
                processes: Array.isArray(payload.processes_data) ? payload.processes_data.length : 0,
                weeks: Array.isArray(payload.weeks) ? payload.weeks.length : 0
              }
            );
          }
        }
      } catch (error) {
        if (typeof window !== 'undefined' && window.console) {
          window.console.error('[Bootstrap] No se pudo recargar el payload.', error);
        }
        console.warn('No se pudo recargar el payload de bootstrap:', error);
      }
    };

    fetchBootstrap();
    return () => {
      cancelled = true;
    };
  }, [data.authenticated, data.processes_data, data.weeks, autoFetchAttempted]);

  const value = useMemo(() => ({
    ...data,
    ready,
    lastUpdated,
    refreshBootstrap
  }), [data, ready, lastUpdated]);

  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  );
};

export const useBootstrap = () => useContext(BootstrapContext);

export const useBootstrapStatus = () => {
  const { ready, lastUpdated, refreshBootstrap } = useContext(BootstrapContext);
  return { ready, lastUpdated, refreshBootstrap };
};

const hasMeaningfulPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (payload.authenticated) {
    return true;
  }
  if (payload.user_profile) {
    return true;
  }
  if (payload.maintenance_mode && typeof payload.maintenance_mode === 'object') {
    return true;
  }
  if (Array.isArray(payload.weeks) && payload.weeks.length > 0) {
    return true;
  }
  if (Array.isArray(payload.processes_data) && payload.processes_data.length > 0) {
    return true;
  }
  return false;
};
